import React, { memo } from 'react';

import { styled } from '@compiled/react';
import { graphql, useFragment } from 'react-relay';
import { colors } from '@atlaskit/theme';
import { token } from '@atlaskit/tokens';
import { ff } from '@atlassian/jira-feature-flagging';
import { IssueTypePickerReadOnly } from '@atlassian/jira-issue-field-issue-type';
import { ParentReadView } from '@atlassian/jira-issue-field-parent-readview-full';
import type { parent_nativeIssueTable_ParentCell$key } from '@atlassian/jira-relay/src/__generated__/parent_nativeIssueTable_ParentCell.graphql';

import ErrorCell from '../../error-cell';
import { TableCellFocusBox } from '../../table-cell-focus-box';
import { StyledLink } from './styled';

export type Props = {
	fieldRef: parent_nativeIssueTable_ParentCell$key;
};

export const ParentCell = memo<Props>(({ fieldRef }) => {
	const data = useFragment<parent_nativeIssueTable_ParentCell$key>(
		graphql`
			fragment parent_nativeIssueTable_ParentCell on JiraParentIssueField
			@argumentDefinitions(
				isIssueViewParentComponentEnabled: {
					type: "Boolean!"
					provider: "@atlassian/jira-relay-provider/src/parent-from-issue-view.relayprovider"
				}
			) {
				parentIssue {
					key
					summaryField @include(if: $isIssueViewParentComponentEnabled) {
						text
					}
					fieldsById(ids: ["issuetype", "summary"]) @skip(if: $isIssueViewParentComponentEnabled) {
						edges {
							node {
								__typename
								fieldId
								... on JiraIssueTypeField {
									issueType {
										avatar {
											small
										}
										name
									}
								}
								... on JiraSingleLineTextField {
									text
								}
							}
						}
					}
				}
				...parent_issueFieldParentReadviewFull_ParentReadView
					@include(if: $isIssueViewParentComponentEnabled)
			}
		`,
		fieldRef,
	);
	/* eslint-enable @atlassian/relay/unused-fields */
	const { parentIssue } = data;

	if (!parentIssue) {
		return null;
	}

	if (ff('jsv-one-issue-table-spreadsheet-ui_ygrnf')) {
		if (parentIssue.key == null || parentIssue.summaryField?.text == null) {
			return <ErrorCell cellType="parent" reason="Missing required fields" />;
		}

		return <ParentReadView fragmentRef={data} />;
	}

	const summaryField = parentIssue?.fieldsById?.edges?.find(
		(edge) => edge?.node?.fieldId === 'summary',
	)?.node;

	const issueType = parentIssue?.fieldsById?.edges?.find(
		(edge) => edge?.node?.fieldId === 'issuetype',
	)?.node?.issueType;

	if (parentIssue?.key == null || summaryField?.text == null) {
		return <ErrorCell cellType="parent" reason="Missing required fields" />;
	}

	return (
		<StyledDiv>
			{issueType?.name != null && issueType?.avatar?.small != null && (
				<IssueTypePickerReadOnly
					value={{ name: issueType.name, iconUrl: issueType.avatar.small }}
				/>
			)}
			<StyledLink href={`/browse/${parentIssue.key}`}>{summaryField.text}</StyledLink>
		</StyledDiv>
	);
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const StyledDiv = styled(TableCellFocusBox)({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
	color: token('color.text', colors.N800),
	display: 'flex',
	alignItems: 'center',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
	div: {
		display: 'flex',
		alignItems: 'center',
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-important-styles -- Ignored via go/DSP-18766
		paddingLeft: `${token('space.0', '0')} !important`,
	},
});
